import PMBSelect from 'components/PMBSelect'
import { useFetchProjectsSelectionQuery } from 'features/private-views/projects-selection/projectsSelectionApiSlice'
import { useEffect, useState } from 'react'

const PMBProjectSelections = ({
  value,
  onChange,
  projectId,
  placeholder = 'Pilih project',
  isMulti = false,
  isInvalid = false,
  isClearable = true,
  title,
  isDisabled = false,
  style,
  className,
  menuPlacement = 'auto',
  menuPosition = 'absolute',
  placeholderColor,
  onLoadSuccess,
  onLoadError,
  testId = 'pmb-project-selections',
  ...props
}) => {
  const [options, setOptions] = useState([])
  const [internalValue, setInternalValue] = useState(value)
  const [hasUserCleared, setHasUserCleared] = useState(false)

  const {
    data: projectsData,
    isLoading,
    isError,
    error,
  } = useFetchProjectsSelectionQuery()

  const normalizeValue = (val, opts, multi) => {
    const findOption = (v) => {
      if (v === null || v === undefined) return null
      if (typeof v === 'object') {
        const match = opts.find((opt) => String(opt.value) === String(v.value))
        if (match) return match

        const matchByName = opts.find(
          (opt) =>
            opt.label === v.label ||
            opt.title === v.label ||
            opt.label === v.value ||
            opt.displayLabel === v.displayLabel ||
            opt.title === v.value,
        )

        if (matchByName) return matchByName

        return v
      }
      return opts.find((opt) => String(opt.value) === String(v)) || null
    }

    if (multi) {
      if (!Array.isArray(val)) return []
      return val.map((v) => findOption(v)).filter(Boolean)
    }

    return findOption(val)
  }

  useEffect(() => {
    if (projectsData) {
      const transformedOptions = projectsData.map((project) => ({
        value: project.id,
        label: project.label,
        title: project.title,
        displayLabel: project.title
          ? `${project.label} - ${project.title}`
          : project.label,
        id: project.id,
        userId: project.userId,
        userName: project.userName,
      }))

      setOptions(transformedOptions)

      if (onLoadSuccess) {
        onLoadSuccess(projectsData)
      }
    }
  }, [projectsData, onLoadSuccess])

  useEffect(() => {
    if (projectId && options.length > 0 && !value && !hasUserCleared) {
      const foundProject = options.find((opt) => opt.value === projectId)
      if (foundProject) {
        setInternalValue(foundProject)
        if (onChange) {
          onChange(foundProject)
        }
      }
    }
  }, [projectId, options, value, onChange, hasUserCleared])

  useEffect(() => {
    const normalized = normalizeValue(value, options, isMulti)
    setInternalValue(normalized)
    if (value !== null && value !== undefined) {
      setHasUserCleared(false)
    }

    // Recovered value detection
    if (
      !isMulti &&
      value &&
      normalized &&
      // Check if value was "name-only" (string value) and normalized is "full-option" (number value usually)
      value.value !== normalized.value
    ) {
      if (onChange) {
        onChange(normalized)
      }
    }
  }, [value, options, isMulti])

  useEffect(() => {
    if (isError && onLoadError) {
      onLoadError(error)
    }
  }, [isError, error, onLoadError])

  // CUSTOM FORMAT FOR OPTION LABEL IN DROPDOWN
  const formatOptionLabel = (option, { context }) => {
    if (props.formatOptionLabel) return props.formatOptionLabel(option, { context })
    if (!option) return null

    return (
      <div className="d-flex flex-column py-1 w-100" style={{ lineHeight: '1.2' }}>
        <div className="fw-bold text-dark" style={{ fontSize: '0.85rem' }}>
          {option.label}
        </div>
        {option.title && (
          <div className="text-muted text-truncate" style={{ fontSize: '0.75rem' }}>
            {option.title}
          </div>
        )}
      </div>
    )
  }

  // CUSTOM FILTER TO SEARCH BOTH LABEL AND TITLE
  const filterOption = (option, inputValue) => {
    const searchValue = inputValue.toLowerCase()
    const label = option.data.label?.toLowerCase() || ''
    const title = option.data.title?.toLowerCase() || ''
    return label.includes(searchValue) || title.includes(searchValue)
  }

  return (
    <PMBSelect
      value={internalValue}
      onChange={(selected) => {
        setInternalValue(selected)
        if (selected === null) {
          setHasUserCleared(true)
        }
        if (onChange) {
          onChange(selected)
        }
      }}
      options={options}
      formatOptionLabel={formatOptionLabel}
      filterOption={filterOption}
      isMulti={isMulti}
      isInvalid={isInvalid}
      isClearable={isClearable}
      placeholder={isLoading ? 'Memuat data...' : placeholder}
      title={title}
      isDisabled={isDisabled || isLoading}
      style={style}
      className={className}
      menuPlacement={menuPlacement}
      menuPosition={menuPosition}
      placeholderColor={placeholderColor}
      testId={testId}
      {...props}
    />
  )
}

export default PMBProjectSelections
